/**
 * Impersonation Composable
 * Provides methods for admin user impersonation functionality
 */

import { ref, computed } from 'vue';
import { useApi } from './useApi';

// localStorage key for impersonation indicator
const IMPERSONATION_KEY = 'impersonation_active';

// Check localStorage for existing impersonation session
function checkLocalStorageImpersonation() {
  try {
    return localStorage.getItem(IMPERSONATION_KEY) === 'true';
  } catch {
    return false;
  }
}

// Save impersonation state to localStorage
function saveImpersonationToStorage(active) {
  try {
    if (active) {
      localStorage.setItem(IMPERSONATION_KEY, 'true');
    } else {
      localStorage.removeItem(IMPERSONATION_KEY);
    }
  } catch {
    // Ignore localStorage errors
  }
}

// Global impersonation state
const impersonationState = ref({
  active: false,
  targetUser: null,
  originalUser: null,
  startedAt: null
});

const isImpersonating = computed(() => impersonationState.value.active);

// Check if there might be an active impersonation session (from localStorage)
// This is useful for determining whether to call getStatus() on mount
const mightBeImpersonating = computed(() => {
  return impersonationState.value.active || checkLocalStorageImpersonation();
});

export function useImpersonation() {
  const api = useApi(); // Create api instance per use, not at module level
  const loading = ref(false);
  const error = ref(null);

  /**
   * Get current impersonation status
   */
  async function getStatus() {
    try {
      loading.value = true;
      error.value = null;

      const response = await api.get('/api/admin/impersonate/status');

      // Backend wraps response in { success: true, data: { active: ..., impersonation: ... } }
      const data = response.data || response;

      if (data.active && data.impersonation) {
        impersonationState.value = {
          active: true,
          targetUser: data.impersonation.targetUser,
          originalUser: data.impersonation.originalUser,
          startedAt: data.impersonation.startedAt
        };
        saveImpersonationToStorage(true);
      } else {
        impersonationState.value = {
          active: false,
          targetUser: null,
          originalUser: null,
          startedAt: null
        };
        saveImpersonationToStorage(false);
      }

      return data;
    } catch (err) {
      // Silently ignore auth errors - these are expected when:
      // - User doesn't have admin permissions (403)
      // - User is not logged in (401)
      const authErrors = [
        'Insufficient permissions',
        'Admin privileges required',
        'Your session has expired. Please log in again.',
        'No authentication token provided',
        'Authentication required'
      ];
      if (authErrors.some(msg => err.message?.includes(msg))) {
        // Don't log auth errors - they're expected for non-admin/non-authenticated users
        // Also clear localStorage since this user doesn't have an active impersonation session
        saveImpersonationToStorage(false);
        return null;
      }

      // Log other errors
      error.value = err.message || 'Failed to get impersonation status';
      console.error('Error getting impersonation status:', err);
      return null;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Search for users to impersonate
   */
  async function searchUsers(searchQuery = '', limit = 50) {
    try {
      loading.value = true;
      error.value = null;

      const params = { search: searchQuery, limit };
      const response = await api.get('/api/admin/impersonate/users', params);

      // Backend wraps response in { success: true, data: { users: [...] } }
      return response.data?.users || response.users || [];
    } catch (err) {
      error.value = err.message || 'Failed to search users';
      console.error('Error searching users:', err);
      return [];
    } finally {
      loading.value = false;
    }
  }

  /**
   * Start impersonating a user
   */
  async function startImpersonation(userId) {
    try {
      loading.value = true;
      error.value = null;

      const response = await api.post(`/api/admin/impersonate/${userId}`);

      // Backend wraps response in { success: true, data: { impersonation: ... } }
      const data = response.data || response;

      if (data.impersonation) {
        impersonationState.value = {
          active: true,
          targetUser: data.impersonation.targetUser,
          originalUser: data.impersonation.originalUser,
          startedAt: data.impersonation.startedAt
        };

        // Save to localStorage so we know to check status after page reload
        saveImpersonationToStorage(true);

        // Reload the current page to apply the impersonated context
        window.location.reload();

        return { success: true, data };
      }

      return { success: false, error: 'Impersonation failed' };
    } catch (err) {
      error.value = err.message || 'Failed to start impersonation';
      console.error('Error starting impersonation:', err);
      return { success: false, error: error.value };
    } finally {
      loading.value = false;
    }
  }

  /**
   * Stop impersonating and return to admin session
   */
  async function stopImpersonation() {
    try {
      loading.value = true;
      error.value = null;

      const data = await api.post('/api/admin/impersonate/stop');

      impersonationState.value = {
        active: false,
        targetUser: null,
        originalUser: null,
        startedAt: null
      };

      // Clear localStorage impersonation indicator
      saveImpersonationToStorage(false);

      // Reload the page to return to admin context
      window.location.reload();

      return { success: true, data };
    } catch (err) {
      error.value = err.message || 'Failed to stop impersonation';
      console.error('Error stopping impersonation:', err);
      return { success: false, error: error.value };
    } finally {
      loading.value = false;
    }
  }

  /**
   * Format duration for display
   */
  function getImpersonationDuration() {
    if (!impersonationState.value.startedAt) return '';

    const start = new Date(impersonationState.value.startedAt);
    const now = new Date();
    const diffMs = now - start;

    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  }

  return {
    // State
    loading,
    error,
    impersonationState,
    isImpersonating,
    mightBeImpersonating,

    // Methods
    getStatus,
    searchUsers,
    startImpersonation,
    stopImpersonation,
    getImpersonationDuration
  };
}
